import { useState, useEffect, useMemo, useCallback, useRef, ChangeEventHandler } from "react";
import { usePost } from "../API";
import { DistroExprLink } from "../Components/ExploreLink"
import { ExpandedSurvey, TrackedLink } from "@aidkitorg/types/lib/survey";
import { Button, Loading } from "@aidkitorg/component-library";
import { SqlViewer, NotificationSimulation, RecipientsTable } from "./CommonComponents";
import { Followup } from "./Followup";
import { EnableKey } from "./EnableKey";
import { useDebouncedCallback, useDebouncedMemo } from "../Hooks/Debounce";
import { findAndGoToComponent } from "../ConfigSearch";
import { expandTemplatesStrict, extractNotificationsAndPaymentsWithSearchTrees } from "@aidkitorg/types/lib/translation/v0_to_legacy";
import * as v0 from "@aidkitorg/types/lib/survey";
import { enableKeyIsValid, enableKeyIsValidForFollowup } from "@aidkitorg/types/lib/eval";
import type { NotificationRecipientsResult } from "@aidkitorg/aidkit/lib/notifications/NotificationService";
import { NotificationRecipientsDownloadModal } from "../AdminPanel/Notifications/components/NotificationRecipientsDownloadModal";

function NotificationDetail({task, survey, enableEmailMarkdownGlobally}: { task: NonNullable<ExpandedSurvey['notifications']>[number] & { searchTree?: string[] }, survey: ExpandedSurvey['survey'], enableEmailMarkdownGlobally: boolean }) {
  let simulateNotifications = usePost('/program/admin/simulate_notifications');
  let getNotificationRecipients = usePost('/api/admin_panel/notifications/recipients');

  let [simulatedMessage, setSimulatedMessage] = useState<Awaited<ReturnType<typeof simulateNotifications>> | null>(null);
  let [liveUpdating, setLiveUpdating] = useState<null | "simulation" | "eligibility">(null);
  let [simulating, setSimulating] = useState(false);
  let [testContactMethod, setTestContactMethod] = useState<string>('sms');
  const [fullQueryResult, setFullQueryResult] = useState<NotificationRecipientsResult | null>(null);
  const [runningFullQuery, setRunningFullQuery] = useState(false);
  const [fullQueryError, setFullQueryError] = useState<string | null>(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [recipientsTab, setRecipientsTab] = useState<'sent' | 'eligible'>('eligible');

  useEffect(() => {
    if (['email', 'sms', 'whatsapp'].includes(task.contactMethod)) {
      setTestContactMethod(task.contactMethod);
    }
  }, [task.contactMethod]);

  const simulate = useCallback(async () => {
    setSimulating(true);
    setSimulatedMessage(await simulateNotifications({
      uids: task.testUIDs || [],
      simulateAllLangs: true,
      emailKey: typeof task.recipient === 'object' ? task.recipient.emailField : '',
      phoneKey: typeof task.recipient === 'object' ? task.recipient.phoneField : '',
      content: task.initial_notification.message,
      emailContent: task.initial_notification.email_message,
      enableEmailMarkdown: !!task.enableEmailMarkdown || enableEmailMarkdownGlobally,
      links: (task.initial_notification.subsurveys || []).reduce((obj, curr) => {
        obj[curr.variable] = curr.name;
        return obj
      }, {} as Record<string, string | TrackedLink>)
    }));
    setSimulating(false);
  }, [task, enableEmailMarkdownGlobally])
  const debouncedSimulate = useDebouncedCallback(() => simulate(), 2000, [simulate]);
  useEffect(() => {
    if (liveUpdating === "simulation") {
      setSimulating(true);
      debouncedSimulate();
    }
  }, [JSON.stringify(task), enableEmailMarkdownGlobally, liveUpdating])

  const runFullQuery = useCallback(async () => {
    setRunningFullQuery(true);
    setFullQueryError(null);
    setFullQueryResult(null);

    // InlineNotifications don't have recipient/enableKey, provide defaults
    if (!task.recipient) {
      setFullQueryError('Cannot run full query for inline notifications without a recipient');
      setRunningFullQuery(false);
      return;
    }

    // Convert task to NotificationGroup format for the API
    const notificationGroup: v0.NotificationGroup = {
      kind: 'Notification',
      version: 1,
      name: task.name,
      targetPrefix: task.targetPrefix,
      recipient: task.recipient,
      contactMethod: task.contactMethod,
      initial_notification: task.initial_notification,
      followups: task.kind !== 'InlineNotification' ? task.followups : undefined,
      enableKey: task.kind !== 'InlineNotification' ? task.enableKey : '',
    };

    const resp = await getNotificationRecipients({
      targetFieldPrefix: task.targetPrefix,
      notificationGroup,
    });

    if (resp && resp.success && resp.data) {
      setFullQueryResult(resp.data);
    } else {
      setFullQueryError(resp?.message || 'Failed to run full query');
    }

    setRunningFullQuery(false);
  }, [task, getNotificationRecipients]);

  const debouncedRunFullQuery = useDebouncedCallback(() => runFullQuery(), 2000, [runFullQuery]);

  useEffect(() => {
    if (liveUpdating === "eligibility") {
      setRunningFullQuery(true);
      debouncedRunFullQuery();
    }
  }, [JSON.stringify(task), liveUpdating]);

  const goToNotification = async (notificationName: string) => {
    const tab = document.getElementsByTagName('distro-editor')[0].shadowRoot!.querySelector("[data-tab-name=notifications]");
    if (tab) tab.dispatchEvent(new Event('click', { bubbles: true }));
    await new Promise(r => setTimeout(r, 50));
    document.getElementsByTagName('distro-editor')[0].shadowRoot!
      .querySelector(`[data-name="${notificationName}"]`)?.scrollIntoView({ block: 'center' });
  }

  return <div className="border border-gray-300 p-2 space-y-2">
    <div className="space-x-2 flex">
      <a href="#" onClick={() => {
        if (task.searchTree) findAndGoToComponent(task.searchTree, survey)
        else goToNotification(task.name);
      }}>
        <h3>{task.name} ({task.targetPrefix})</h3>
      </a>
      <DistroExprLink name={task.name} expr={task.initial_notification.enabled_when} />
    </div>
    {task.kind !== 'InlineNotification' ?
      <EnableKey component={task} />
      : <b>Inline Notif</b>}
    <ul className="flex space-x-1">
      <li>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            if (liveUpdating === "eligibility") {
              setLiveUpdating(null);
              setFullQueryResult(null);
            } else {
              setLiveUpdating("eligibility");
            }
          }}
          loading={runningFullQuery}
        >
          {runningFullQuery ? 'Loading...' : liveUpdating === "eligibility" ? 'Watching for changes...' : 'Check Eligibility'}
        </Button>
      </li>
      <li>
        <Button
          variant="secondary"
          onClick={() => setLiveUpdating(liveUpdating === "simulation" ? null : "simulation")}
          size="sm"
          loading={simulating}
        >
          {simulating ? 'Simulating...' : liveUpdating === "simulation" ? 'Watching for changes...' : 'Simulate Formatted Notification'}
        </Button>
      </li>
    </ul>
    {task.recipient === 'Unsubmitted Applicant' && !(['email', 'sms', 'whatsapp'].includes(task.contactMethod)) &&
      <select className="ml-2 inline-flex items-center shadow-sm overflow-hidden bg-white px-2.5 py-1.5 text-black text-xs font-medium text-base text-gray-700 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded"
        onChange={(e) => setTestContactMethod(e.target.value)}>
        <option value="sms">SMS</option>
        <option value="email">Email</option>
        <option value="whatsapp">WhatsApp</option>
      </select>
    }
    <NotificationSimulation
      task={task}
      enableEmailMarkdown={task.enableEmailMarkdown || enableEmailMarkdownGlobally}
      simulated={simulatedMessage}
      simulationSource={liveUpdating} />
    {/* Full Query Results */}
    {fullQueryError && (
      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
        {fullQueryError}
      </div>
    )}
    {fullQueryResult && (
      <div className="border border-gray-200 rounded p-3 space-y-3 bg-gray-50">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm">Eligibility Results</h4>
          <Button variant="secondary" size="sm" onClick={() => setShowDownloadModal(true)}>
            Download
          </Button>
        </div>
        <div className="space-y-2">
          {(['email', 'sms', 'whatsapp'] as const).map(method => {
            const queryInfo = fullQueryResult.queries?.[method];
            if (!queryInfo) return null;
            const eligibleCount = fullQueryResult.eligible.filter(r => r.channel === method).length;
            return (
              <div key={method} className={`p-2 rounded border ${queryInfo.error ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium capitalize">{method}</span>
                    <span className="text-gray-600 ml-2">{eligibleCount} eligible</span>
                  </div>
                </div>
                {queryInfo.error && (
                  <div className="text-red-600 text-xs mt-1">{queryInfo.error}</div>
                )}
                <SqlViewer sql={queryInfo.query} />
              </div>
            );
          })}
        </div>
        <RecipientsTable
          eligible={fullQueryResult.eligible}
          sent={fullQueryResult.sent}
          variables={fullQueryResult.variables}
          recipientsTab={recipientsTab}
          setRecipientsTab={setRecipientsTab}
        />
      </div>
    )}
    {((task.kind !== 'InlineNotification' && task.followups) || []).map((followup) => {
      return <Followup task={task} flup={followup} key={followup.suffix} enableEmailMarkdownGlobally={enableEmailMarkdownGlobally} />
    })}
    {/* Download Modal */}
    {fullQueryResult && (
      <NotificationRecipientsDownloadModal
        notificationName={task.name}
        sentData={fullQueryResult.sent}
        eligibleData={fullQueryResult.eligible}
        variables={fullQueryResult.variables}
        downloadModalOpen={showDownloadModal}
        setDownloadModalOpen={setShowDownloadModal}
      />
    )}
  </div>
}

export function NotificationsView({ survey, enableEmailMarkdownGlobally }: { 
  survey: ExpandedSurvey, 
  enableEmailMarkdownGlobally: boolean | undefined 
}) {

  const [notifications, setNotifications] = useState<v0.Notification[] | null>(null);
  let timerRef = useRef<NodeJS.Timeout | null>(null);

  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const filteredResults = useDebouncedMemo(() => {
    return (notifications ?? []).filter(
      item => item.name.toLowerCase().includes(search.toLowerCase())
      || item.targetPrefix.toLowerCase().includes(search.toLowerCase())
    )}, 500, [search, notifications]);

  const { enabled, total } = useMemo(() => {
    let enabled = 0;
    let total = (filteredResults || []).length;

    filteredResults?.forEach((n) => {
      // Only check regular notifications and followups, not inline notifications
      if (n.kind === 'Notification') {
        total += (n.followups || []).length;
  
        // Count initial notification
        if (enableKeyIsValid(n.enableKey, n)) {
          enabled++;
          // Count follow-ups
          n.followups?.forEach((f) => {
            if (enableKeyIsValidForFollowup(f, n)) {
              enabled++;
            }
          });
        }
      }
    });
    return { enabled, total };
  }, [filteredResults]);

  const onChangeSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearch(e.target.value);
    const pane = document.getElementById("top-of-pane");
    if (pane) pane.scrollIntoView();
  }

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setLoading(true);
    setLoaded(false);

    timerRef.current = setTimeout(() => {
      const extractedNotifsAndPayments = extractNotificationsAndPaymentsWithSearchTrees(
        expandTemplatesStrict(survey.survey)
      );
      setNotifications([...(survey.notifications || []), ...extractedNotifsAndPayments[0]])
      setLoading(false);
      setLoaded(true);
    }, 50)

    return () => timerRef.current ? clearTimeout(timerRef.current) : void 0;
  }, [survey])

  if (!notifications?.length && !loading && loaded) {
    return <div className="mt-4 ml-1">No notifications yet!</div>
  }
  return <Loading loading={loading && !notifications} labels={[{ content: "Loading notifications..."}]}>
    <div>
      <div id="top-of-pane" />
      <input type="search" placeholder="Search for notifications" value={search} onChange={onChangeSearch}
        className="block w-full border top-0 left-0 z-50 p-2 mt-1 mb-1 sticky sm:text-sm rounded-md" />
      <h4 className="text-center">{enabled}/{total} Enabled</h4>
      <div>
        {filteredResults.map((t) => 
          <NotificationDetail key={t.name + t.targetPrefix} 
            task={t} 
            survey={survey.survey} 
            enableEmailMarkdownGlobally={!!enableEmailMarkdownGlobally} />
      
        )}
      </div>
    </div>
  </Loading>
}
